import { Node } from "prosemirror-model";
import { EditorState } from "prosemirror-state";
import { ExternalAnnotation, InternalAnnotation } from "./Annotations";
import { createEditorStateFromDocument, MarkKeys, mySchema, NodeKeys } from "./Schema";

export interface ScorableTextFormat {
	/**
	 * The plain text of the document, with all formatting stripped, in a suitable format for tools to read as raw text.
	 * Guaranteed to not contain certain problematic control characters: \0\r\n\b\v
	 */
	text: string;
	/**
	 * The markup metadata for the text, in an opaque format not meant to be read externally.  In order to round-trip and
	 * successfully rerender this markup in the future, it must be supplied with the exact same `text` value it was sent out
	 * with.
	 * Internally, this is the JSON stringification of ScorableTextMarkup, but don't rely on that.
	 */
	markup: string;
	/**
	 * Annotations to apply to the text.
	 */
	annotations: ExternalAnnotation[];
}

/** The markup to deliver alongside of text to recreate a rich text document */
interface ScorableTextMarkup {
	/** Tag identifying this as a STM. */
	$stf: 1;
	/** Version number.  If we later need to adjust behaviors, we can bump this and then keep both old and new deserializers. */
	$ver: 1;
	/**
	 * Hash of the text that this markup should be associated with.  I don't want anyone to ever spend any time trying to debug
	 * why this code isn't working when the caller screwed up.
	 */
	hash: number;
	/** The document node. */
	root: StmNode;
}

function hash(s: string) {
	let ret = 37;
	for (let i = 0; i < s.length; i++) {
		ret = (ret * 23 + s.charCodeAt(i)) | 0;
	}
	return ret;
}

/** Map node names to shorter values to save space in the STM representation. */
const NodeNameToShortName: { [K in NodeKeys]: string | null } = {
	doc: "#",
	paragraph: "p",
	heading: "h",
	text: null,
	hard_break: "br",
	ordered_list: "ol",
	unordered_list: "ul",
	list_item: "li",
};
/** The reverse lookup for NodeNameToShortName */
const ShortNameToNodeName = new Map(
	(Object.entries(NodeNameToShortName) as [NodeKeys, string | null][])
		.filter(e => e[1])
		.map(e => [e[1]!, e[0]])
);

/** Map mark names to shorter values to save space in the STM representation. */
const MarkNameToShortName: { [K in MarkKeys]: string } = {
	em: "i",
	strong: "b",
	u: "u",
	sub: "s",
	sup: "S",
};
/** The reverse lookup for ShortNameToMarkName */
const ShortNameToMarkName = new Map(
	(Object.entries(MarkNameToShortName) as [MarkKeys, string][]).map(e => [e[1], e[0]])
);

/**
 * Text nodes, represented by the length of their content (which is sliced from the STF.text property) and the marks.
 * If no marks are used, can just have the length number.
 */
type StmText = number | [length: number, ...marks: string[]];
/** Layout for non-text nodes. */
interface StmFullNode {
	/** Type */
	t: string;
	/** attribs */
	a?: { [key: string]: any };
	/** content */
	c?: StmNode[];
}
/** Text or element node. */
type StmNode = StmText | StmFullNode;

/** For non-text nodes, create a useful representation in the STF.text area. */
function computeNodeDescriptiveSuffix(nodeName: NodeKeys): string {
	const nodeType = mySchema.nodes[nodeName];
	if (nodeType.isBlock || nodeType === mySchema.nodes.hard_break) {
		return "\n";
	}
	return "";
}

/** Compute how much the Prosemirror index position advances when entering this node. */
function computeNodePreOffset(nodeName: NodeKeys): number {
	const nodeType = mySchema.nodes[nodeName];
	// https://prosemirror.net/docs/guide/#doc.indexing
	if (nodeType === mySchema.topNodeType || nodeType.isText) {
		return 0;
	} else {
		return 1;
	}
}
/** Compute how much the Prosemirror index position advances when leaving this node. */
function computeNodePostOffset(nodeName: NodeKeys): number {
	const nodeType = mySchema.nodes[nodeName];
	// https://prosemirror.net/docs/guide/#doc.indexing
	if (nodeType === mySchema.topNodeType || nodeType.isText || nodeType.isLeaf) {
		return 0;
	} else {
		return 1;
	}
}

/** Used internally to transform Annotations during serialization and deserialization */
interface RemapInfo {
	from: number;
	offset: number;
}
function applyRemaps(pos: number, remaps: RemapInfo[]) {
	let offset = 0;
	for (const r of remaps) {
		if (pos >= r.from) {
			offset = r.offset;
		} else {
			break;
		}
	}
	return pos - offset;
}

/** A holder to track various mutable properties as we create a STF document. */
interface StfBuilder {
	/** The text so far */
	s: string;
	/** Position in the source document  */
	nextRemap: RemapInfo;
	/** Remapping information that we'll use to map annotations */
	remaps: RemapInfo[];
}
function nodeToStm(node: Node, b: StfBuilder): StmNode {
	if (node.isText) {
		// `s` will not contain an `\r` or a `\n` under any legal circumstances.  Prosemirror turns these into paragraph tags.
		// `s` may contain `\t` and that will be handled just fine.
		// Other control characters might exist and are probably mostly benign,
		//     but let's strip a few that the system doesn't handle in a productive way,
		//     and that downstream systems may cause a mess with, just to be sure.
		const s = node.text!.replace(/[\0\r\n\b\v]/g, "\ufffd");
		const ret: StmText = node.marks.length
			? [s.length, ...node.marks.map(m => MarkNameToShortName[m.type.name as MarkKeys])]
			: s.length;
		b.s += s;
		b.nextRemap.from += s.length;
		return ret;
	} else {
		// TODO: All of these casts `node.type.name as NodeKeys` are really a hole in the .d.ts file.  They shouldn't be needed

		const ret: StmFullNode = {
			t: NodeNameToShortName[node.type.name as NodeKeys]!,
		};
		for (const k in node.attrs) {
			// set ret.a if at least one key exists on attrs.  Otherwise, zero keys and no object are equvialent.
			ret.a = node.attrs;
			break;
		}
		{
			const offset = computeNodePreOffset(node.type.name as NodeKeys);
			b.nextRemap.from += offset;
			b.nextRemap.offset += offset;
			b.remaps.push({ ...b.nextRemap });
		}
		if (node.content.size) {
			ret.c = [];
			node.content.forEach(n => ret.c!.push(nodeToStm(n, b)));
		}
		{
			const offset = computeNodePostOffset(node.type.name as NodeKeys);
			const suffix = computeNodeDescriptiveSuffix(node.type.name as NodeKeys);
			b.nextRemap.from += offset;
			b.nextRemap.offset += offset - suffix.length;
			b.remaps.push({ ...b.nextRemap });
			b.s += suffix;
		}
		return ret;
	}
}
/** Serialize a ProseMirror state into STF. */
export function toStf(state: EditorState, annotations: InternalAnnotation[]): ScorableTextFormat {
	const b: StfBuilder = { s: "", nextRemap: { from: 0, offset: 0 }, remaps: [] };
	const root = nodeToStm(state.doc, b);
	const markup: ScorableTextMarkup = {
		$stf: 1,
		$ver: 1,
		hash: hash(b.s),
		root,
	};
	return {
		text: b.s,
		markup: JSON.stringify(markup),
		annotations: annotations.map(a => {
			const from = Math.max(0, applyRemaps(a.from, b.remaps));
			const to = Math.min(b.s.length, applyRemaps(a.to, b.remaps));
			return { from, length: to - from, payload: a.payload };
		}),
	};
}

/** Internal helper function used by fromStf and fromRawText to process their result. */
function createStateHelper(
	doc: Node,
	annotations: ExternalAnnotation[],
	...allRemaps: RemapInfo[][]
): { state: EditorState; annotations: InternalAnnotation[] } {
	return {
		state: createEditorStateFromDocument(doc),
		annotations: annotations.map(a => {
			let from = a.from;
			let to = a.from + a.length;
			for (const remaps of allRemaps) {
				from = applyRemaps(from, remaps);
				to = applyRemaps(to, remaps);
			}
			from = Math.max(0, from);
			to = Math.min(doc.content.size, to);
			return { from, to, payload: a.payload };
		}),
	};
}

/** A holder for the string value and the mutable position value as we deserialize a STF document. */
interface StfUnbuilder {
	s: string;
	pos: number;
	/** Computed offset in the destination ProseMirror document  */
	offset: number;
	/** Remapping information that we'll use to map ("unmap"?) annotations */
	remaps: RemapInfo[];
}
function nodeFromStm(node: StmNode, b: StfUnbuilder): Node {
	if (Array.isArray(node)) {
		const [length, ...markShortNames] = node;
		const marks = markShortNames.map(name => {
			const markName = ShortNameToMarkName.get(name);
			if (!markName) {
				throw new Error(`Bad mark shortName '${name}`);
			}
			return mySchema.mark(markName);
		});
		const ret = mySchema.text(b.s.substr(b.pos, length), marks);
		b.pos += length;
		return ret;
	} else if (typeof node === "number") {
		const ret = mySchema.text(b.s.substr(b.pos, node));
		b.pos += node;
		return ret;
	} else {
		const nodeName = ShortNameToNodeName.get(node.t);
		if (!nodeName) {
			throw new Error(`Bad node shortname '${node.t}'`);
		}
		b.offset += 0 /*preContentLength*/ - computeNodePreOffset(nodeName);
		b.remaps.push({ from: b.pos, offset: b.offset });
		const content = node.c?.map(n => nodeFromStm(n, b));
		const ret = mySchema.node(nodeName, node.a, content);
		const postContentLength = computeNodeDescriptiveSuffix(nodeName).length;
		b.pos += postContentLength;
		b.offset += postContentLength - computeNodePostOffset(nodeName);
		b.remaps.push({ from: b.pos, offset: b.offset });
		return ret;
	}
}
/** Deserialize STF into a ProseMirror document. */
export function fromStf(stf: ScorableTextFormat) {
	const { text } = stf;
	const markup: ScorableTextMarkup = JSON.parse(stf.markup);
	if (markup.$stf !== 1 || markup.$ver !== 1) {
		throw new Error("Bad STF input");
	}
	if (markup.hash !== hash(text)) {
		throw new Error("STF was made for a different text string");
	}
	const b: StfUnbuilder = {
		s: text,
		pos: 0,
		offset: 0,
		remaps: [],
	};
	const doc = nodeFromStm(markup.root, b);
	return createStateHelper(doc, stf.annotations, b.remaps);
}

/** Import raw text (NOT HTML) and map provided annotations. */
export function fromRawText(text: string, annotations: ExternalAnnotation[]) {
	// Real student essays never contain "\r".  Sample data may contain "\r", but always followed by
	// "\n", so just get rid of it.
	const crSplits = text.split("\r");
	const crRemaps: RemapInfo[] = [];
	let from = 0;
	let offset = 0;
	for (const s of crSplits) {
		crRemaps.push({ from, offset });
		from += s.length + 1;
		offset += 1;
	}
	text = crSplits.join("");

	const remaps: RemapInfo[] = [];
	from = 0;
	offset = 0;

	const content = text.split("\n").map(paragraph => {
		offset--; // Enter <p>
		remaps.push({ from, offset });
		from += paragraph.length + 1;
		return mySchema.node(
			"paragraph",
			undefined,
			paragraph ? [mySchema.text(paragraph)] : undefined
		);
	});

	const doc = mySchema.node("doc", undefined, content);
	return createStateHelper(doc, annotations, crRemaps, remaps);
}
